import { Link } from 'react-router-dom';
import { ShoppingBag, ArrowLeft } from 'lucide-react';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center gap-2">
              <ShoppingBag className="h-8 w-8 text-slate-900" />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Formula Library</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Back Button */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <h1 className="text-4xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="terms-title">
          Terms and Conditions
        </h1>
        
        <div className="prose prose-slate max-w-none">
          <p className="text-slate-600 mb-6">
            <strong>Last Updated:</strong> July 2026
          </p>

          <p className="text-slate-600 mb-8">
            Welcome to Formula Library. By accessing or using our website and services, you agree to be bound by these Terms and Conditions. Please read them carefully before making any purchase.
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              1. Acceptance of Terms
            </h2>
            <p className="text-slate-600 mb-4">
              By accessing this website and purchasing our digital products, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you must not use our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              2. Products and Services
            </h2>
            <p className="text-slate-600 mb-4">
              Formula Library offers digital products including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Business formulas and manufacturing guides</li>
              <li>Training materials and educational content</li>
              <li>Supplier directories and industry contacts</li>
              <li>Digital downloads in PDF and other formats</li>
            </ul>
            <p className="text-slate-600">
              All products are delivered digitally via email to the address provided at checkout.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              3. Pricing and Payment
            </h2>
            <p className="text-slate-600 mb-4">
              All prices are displayed in the user's local currency based on their geographic location, with USD as the base currency for conversion. Payment processing is handled through PayFast, a secure payment gateway. By completing a purchase, you agree to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Provide accurate payment information</li>
              <li>Pay all charges at the prices in effect when incurred</li>
              <li>Accept that exchange rates may fluctuate between viewing and payment</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              4. Digital Product License
            </h2>
            <p className="text-slate-600 mb-4">
              Upon successful payment, you are granted a non-exclusive, non-transferable license to use the digital products for personal or business purposes. You may NOT:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Redistribute, resell, or share the products with third parties</li>
              <li>Modify, adapt, or create derivative works for commercial distribution</li>
              <li>Use the content to compete directly with Formula Library</li>
              <li>Remove any copyright or proprietary notices</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              5. Intellectual Property
            </h2>
            <p className="text-slate-600 mb-4">
              All content on this website, including but not limited to text, graphics, logos, images, and digital products, is the property of Formula Library and is protected by international copyright laws. Unauthorized use may result in legal action.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              6. Disclaimer of Warranties
            </h2>
            <p className="text-slate-600 mb-4">
              Our products are provided "as is" without warranties of any kind. While we strive for accuracy:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>We do not guarantee specific business results or outcomes</li>
              <li>Formulas should be tested in small batches before commercial production</li>
              <li>Users are responsible for compliance with local regulations</li>
              <li>Professional consultation is recommended for specific applications</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              7. Limitation of Liability
            </h2>
            <p className="text-slate-600 mb-4">
              Formula Library shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our products or services. Our total liability shall not exceed the amount paid for the specific product in question.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              8. Privacy Policy
            </h2>
            <p className="text-slate-600 mb-4">
              We collect and process personal data in accordance with applicable data protection laws. Information collected includes:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Email address for product delivery and communication</li>
              <li>Payment information (processed securely by PayFast)</li>
              <li>Usage data for improving our services</li>
            </ul>
            <p className="text-slate-600">
              We do not sell or share your personal information with third parties for marketing purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              9. Governing Law
            </h2>
            <p className="text-slate-600 mb-4">
              These Terms and Conditions shall be governed by and construed in accordance with the laws of South Africa. Any disputes arising shall be subject to the exclusive jurisdiction of the courts of South Africa.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              10. Contact Information
            </h2>
            <p className="text-slate-600 mb-4">
              For questions or concerns regarding these Terms and Conditions, please contact us:
            </p>
            <ul className="list-none text-slate-600 space-y-2">
              <li><strong>WhatsApp:</strong> +27 730 758 123</li>
              <li><strong>Website:</strong> formulalibrary.online</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              11. Changes to Terms
            </h2>
            <p className="text-slate-600">
              We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting to the website. Continued use of our services after any changes constitutes acceptance of the new terms.
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400">
            © 2026 Formula Library. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">Terms & Conditions</Link>
            <Link to="/refund-policy" className="text-slate-400 hover:text-white transition-colors">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsConditions;
